import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useSpaceStore } from "../../stores";
import type { StarSystem } from "../../types";

const BASE_SCALE = 10;
/** Max distance (in scaled units) for a constellation edge */
const MAX_EDGE_DIST = 25 * BASE_SCALE;
const MAX_OPACITY = 0.12;

interface ConstellationLinesProps {
	systems: StarSystem[];
}

interface StarNode {
	id: string;
	x: number;
	y: number;
}

/**
 * Computes k-nearest-neighbor edges using spatial grid bucketing.
 * Grid cells are MAX_EDGE_DIST wide — each star only checks its 9 neighboring cells.
 * Returns a flat Float32Array of line segment positions (x1,y1,z1,x2,y2,z2,...).
 */
function computeEdges(systems: StarSystem[], k: number): Float32Array {
	const maxDistSq = MAX_EDGE_DIST * MAX_EDGE_DIST;
	const cellSize = MAX_EDGE_DIST;

	// Build spatial grid
	const grid = new Map<string, StarNode[]>();
	const nodes: StarNode[] = [];

	for (const sys of systems) {
		const node: StarNode = {
			id: sys.id,
			x: sys.x * BASE_SCALE,
			y: -(sys.y * BASE_SCALE),
		};
		nodes.push(node);

		const cx = Math.floor(node.x / cellSize);
		const cy = Math.floor(node.y / cellSize);
		const key = `${cx},${cy}`;
		const cell = grid.get(key);
		if (cell) {
			cell.push(node);
		} else {
			grid.set(key, [node]);
		}
	}

	const edgeSet = new Set<string>();
	const edges: number[] = [];

	for (const node of nodes) {
		const cx = Math.floor(node.x / cellSize);
		const cy = Math.floor(node.y / cellSize);

		// Collect candidates from 9 neighboring cells
		const neighbors: { id: string; x: number; y: number; distSq: number }[] = [];

		for (let dx = -1; dx <= 1; dx++) {
			for (let dy = -1; dy <= 1; dy++) {
				const cell = grid.get(`${cx + dx},${cy + dy}`);
				if (!cell) continue;
				for (const other of cell) {
					if (other.id === node.id) continue;
					const distSq = (node.x - other.x) ** 2 + (node.y - other.y) ** 2;
					if (distSq > maxDistSq) continue;

					if (neighbors.length < k) {
						neighbors.push({ id: other.id, x: other.x, y: other.y, distSq });
						if (neighbors.length === k) {
							neighbors.sort((a, b) => a.distSq - b.distSq);
						}
					} else if (distSq < (neighbors[k - 1]?.distSq ?? Number.POSITIVE_INFINITY)) {
						neighbors[k - 1] = { id: other.id, x: other.x, y: other.y, distSq };
						neighbors.sort((a, b) => a.distSq - b.distSq);
					}
				}
			}
		}

		for (const n of neighbors) {
			const edgeKey = node.id < n.id ? `${node.id}:${n.id}` : `${n.id}:${node.id}`;
			if (edgeSet.has(edgeKey)) continue;
			edgeSet.add(edgeKey);
			edges.push(node.x, node.y, 0, n.x, n.y, 0);
		}
	}

	return new Float32Array(edges);
}

export function ConstellationLines({ systems }: ConstellationLinesProps): React.JSX.Element | null {
	const materialRef = useRef<THREE.LineBasicMaterial>(null);
	const { isLowPower } = useDeviceCapabilities();

	const k = isLowPower ? 1 : 2;

	const positions = useMemo(() => computeEdges(systems, k), [systems, k]);

	const geometry = useMemo(() => {
		const geo = new THREE.BufferGeometry();
		geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		return geo;
	}, [positions]);

	// Animate opacity based on zoom level
	useFrame(() => {
		if (!materialRef.current) return;
		const state = useSpaceStore.getState();
		const scale = state.viewMode === "galaxy" ? state.galaxyView.scale : 0;

		let opacity = 0;
		if (scale >= 1 && scale <= 3) {
			// Fade in: 1 → 3
			opacity = ((scale - 1) / 2) * MAX_OPACITY;
		} else if (scale > 3 && scale <= 8) {
			// Full opacity
			opacity = MAX_OPACITY;
		} else if (scale > 8 && scale <= 10) {
			// Fade out: 8 → 10
			opacity = ((10 - scale) / 2) * MAX_OPACITY;
		}

		materialRef.current.opacity = opacity;
		materialRef.current.visible = opacity > 0.001;
	});

	if (positions.length === 0) return null;

	return (
		<lineSegments geometry={geometry} renderOrder={-1}>
			<lineBasicMaterial
				ref={materialRef}
				color="#ffffff"
				transparent
				opacity={0}
				depthWrite={false}
			/>
		</lineSegments>
	);
}
